import ordersApi from '@/services/orders.service'
import type { OrderCreateModel, OrderModel, OrderStateUpdateModel } from '@/types/models/OrderModel'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import { EOrderActions, EOrderMutations, type OrderState } from './OrderStoreTypes'
import { EAlertActions, EStoreGlobalModules } from '@rfc/rfc-components-library/store'
import i18n from '../../locales/i18n'

const state: Ref<PaginationDataType<OrderState>> = ref({
    data: {
        orders: []
    },

    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getOrders: () => state.value.data?.order,
    loading: () => state.value.loading
}

const actions = {
    async [EOrderActions.FETCH_ORDERS](
        { commit }: { commit: Commit },
        params?: Partial<PaginationType>
    ) {
        commit(EOrderMutations.SET_LOADING, true)
        return ordersApi
            .getOrders(params)
            .then(data => {
                commit(EOrderMutations.SET_ORDER, data.data)
                commit(EOrderMutations.SET_PAGINATION, data)
                return data
            })
            .finally(() => {
                commit(EOrderMutations.SET_LOADING, false)
            })
    },

    async [EOrderActions.FETCH_ORDER]({ commit }: { commit: Commit }, { id }: { id: number }) {
        commit(EOrderMutations.SET_LOADING, true)
        return await ordersApi
            .getOrder(id)
            .then(res => {
                commit(EOrderMutations.SET_ORDER, res.data)
                return res.data
            })
            .finally(() => commit(EOrderMutations.SET_LOADING, false))
    },

    async [EOrderActions.CREATE_ORDER](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        data: OrderCreateModel
    ) {
        commit(EOrderMutations.SET_LOADING, true)
        return ordersApi
            .createOrder(data)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: EOrderActions.CREATE_ORDER
                })
                return res.data
            })
            .finally(() => commit(EOrderMutations.SET_LOADING, false))
    },

    async [EOrderActions.UPDATE_ORDER](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, data }: { id: number; data: OrderStateUpdateModel }
    ) {
        commit(EOrderMutations.SET_LOADING, true)
        return ordersApi
            .updateOrder(id, data)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: EOrderActions.UPDATE_ORDER
                })
            )
            .finally(() => {
                commit(EOrderMutations.SET_LOADING, false)
            })
    },

    async [EOrderActions.DELETE_ORDER](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, name }: { id: number; name: string }
    ) {
        await ordersApi.deleteOrder(id)
        commit(EOrderMutations.REMOVE_ORDER, id)
        return ordersApi
            .deleteOrder(id)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: EOrderActions.DELETE_ORDER
                })
            )
            .finally(() => commit(EOrderMutations.SET_LOADING, false))
    },

    [EOrderActions.RESET_ORDER]({ commit }: { commit: Commit }) {
        commit(EOrderMutations.RESET_ORDER)
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreGlobalModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`orders.api.success.${key}`, { name })
            },
            { root: true }
        )
    }
}

const mutations = {
    [EOrderMutations.SET_ORDERS](_: OrderState, orders: OrderModel[]) {
        state.value.data!.orders = orders
    },
    [EOrderMutations.SET_ORDER](_: OrderState, order: OrderModel) {
        state.value.data!.order = order
    },
    [EOrderMutations.SET_LOADING](_: OrderState, loading: boolean) {
        state.value.loading = loading
    },
    [EOrderMutations.SET_PAGINATION](_: OrderState, data: PaginationDataType<OrderModel>) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
    },
    [EOrderMutations.REMOVE_ORDER](_: OrderState, id: number) {
        state.value.data!.orders = state.value.data?.orders?.filter(item => item.id !== id) ?? []
    },
    [EOrderMutations.RESET_ORDER]() {
        state.value.data!.order = undefined
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
